import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * Shared hook that fetches the backend's platform-capability matrix once
 * per session and exposes small utility functions for the composer:
 *
 *   - requiresImage(platform)                — does this ONE platform need media?
 *   - anyRequiresImage(platforms)            — does the current selection need media?
 *   - platformsRequiringImage(platforms)     — which specific ones?
 *
 * Why a hook instead of a hard-coded object? Because the backend
 * (server.py::PLATFORM_CAPABILITIES) is the single source of truth. Any
 * new image-required platform added there flows to the frontend without
 * a code change — matches the user's requirement: "Do not hardcode this
 * only inside one React page. Use shared platform-capability logic so
 * future platforms can define whether an image is required."
 *
 * Payload shape (from GET /api/platforms/capabilities):
 *   {
 *     platforms: {
 *       instagram: { label, requires_image, supports_image, requires_image_reason },
 *       facebook:  { label, requires_image, supports_image },
 *       ...
 *     }
 *   }
 */

// Normalization mirror of backend `_normalize_platform_key`. Deliberately
// permissive so any UI label ("X (Twitter)", "IG", "insta", "Instagram")
// maps to the same canonical key we compare against.
const X_ALIASES = new Set(["x", "twitter", "x (twitter)", "x/twitter"]);
const FACEBOOK_ALIASES = new Set(["facebook", "fb", "meta"]);
const INSTAGRAM_ALIASES = new Set(["instagram", "ig", "insta"]);
const LINKEDIN_ALIASES = new Set(["linkedin", "linked in", "linked-in"]);
const TIKTOK_ALIASES = new Set(["tiktok", "tik tok", "tik-tok"]);

export function normalizePlatformKey(raw) {
  if (!raw) return "";
  const key = String(raw).trim().toLowerCase();
  if (X_ALIASES.has(key) || key.includes("twitter") || key.startsWith("x ") || key === "x") return "x";
  if (FACEBOOK_ALIASES.has(key)) return "facebook";
  if (INSTAGRAM_ALIASES.has(key)) return "instagram";
  if (LINKEDIN_ALIASES.has(key)) return "linkedin";
  if (TIKTOK_ALIASES.has(key)) return "tiktok";
  return "";
}

// Sensible fallback while the network call is in flight — matches the
// backend defaults so the UI does the right thing even if the endpoint
// is momentarily slow. Any new image-required platform lives ONLY in
// the backend; this is just a first-paint fallback.
const FALLBACK_CAPS = {
  instagram: { label: "Instagram", requires_image: true, supports_image: true },
  facebook: { label: "Facebook", requires_image: false, supports_image: true },
  x: { label: "X", requires_image: false, supports_image: true },
  linkedin: { label: "LinkedIn", requires_image: false, supports_image: true },
  tiktok: { label: "TikTok", requires_image: true, supports_image: true },
};

let _capsCache = null; // Session-level cache (SSR-safe: only touched on client).

export default function usePlatformCapabilities({ enabled = true } = {}) {
  const [caps, setCaps] = useState(_capsCache || FALLBACK_CAPS);
  const [loading, setLoading] = useState(!_capsCache);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.get("/platforms/capabilities");
      const payload = res?.data?.platforms || {};
      // Normalize keys to lowercase so comparisons are consistent even
      // if the backend ever returns capital-cased keys.
      const normalized = {};
      Object.keys(payload).forEach((k) => {
        normalized[String(k).toLowerCase()] = payload[k];
      });
      _capsCache = normalized;
      setCaps(normalized);
    } catch {
      // Silent fallback — the UI still works with FALLBACK_CAPS.
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!_capsCache && enabled) refresh();
  }, [refresh, enabled]);

  const requiresImage = useCallback(
    (platform) => {
      const k = normalizePlatformKey(platform);
      if (!k) return false;
      return !!(caps?.[k]?.requires_image);
    },
    [caps],
  );

  const platformsRequiringImage = useCallback(
    (platforms) => {
      const seen = new Set();
      const out = [];
      for (const p of platforms || []) {
        const k = normalizePlatformKey(p);
        if (!k || seen.has(k)) continue;
        if (!caps?.[k]?.requires_image) continue;
        seen.add(k);
        out.push(caps[k]?.label || p);
      }
      return out;
    },
    [caps],
  );

  const anyRequiresImage = useCallback(
    (platforms) => platformsRequiringImage(platforms).length > 0,
    [platformsRequiringImage],
  );

  const requiresImageReason = useCallback(
    (platforms) => {
      const keys = (platforms || [])
        .map(normalizePlatformKey)
        .filter((k) => k && caps?.[k]?.requires_image);
      for (const k of keys) {
        const r = caps?.[k]?.requires_image_reason;
        if (r) return r;
      }
      return null;
    },
    [caps],
  );

  return useMemo(
    () => ({
      caps,
      loading,
      refresh,
      requiresImage,
      anyRequiresImage,
      platformsRequiringImage,
      requiresImageReason,
    }),
    [caps, loading, refresh, requiresImage, anyRequiresImage, platformsRequiringImage, requiresImageReason],
  );
}
